/* eslint-disable no-console */
/* eslint-disable @typescript-eslint/no-explicit-any */
import { toast } from "react-toastify";

import {
  isLocalOrJenkinsMode,
  isLocalOrJenkinsModeOrQsDebugLog,
} from "./env.helpers";
import { ToastActionBox, ToastErrorBox, ToastInfoBox } from "./toast-widgets";

/** An actionable toast must outlive the default dismissal, or the action is gone before the user reaches it. */
const TOAST_WITH_ACTION_AUTO_CLOSE_MS = 15_000;

// INFO: debug logging is not displayed on production build
export enum LogLevel {
  Error = "error",
  Warn = "warn",
  Info = "info",
  Debug = "debug",
  Logging = "log",
  Trace = "trace",
  Table = "table",
}

export const logBoolean = (val: any): string => {
  return val ? "yes" : "no";
};

export class Log {
  static log(...params: any[]): void {
    Log._log(LogLevel.Info, ...params);
  }

  static logAndToast(...params: any[]): void {
    Log._log(LogLevel.Info, ...params);
    toast.info(params?.[0]);
  }

  static warn(...params: any[]): void {
    Log._log(LogLevel.Warn, ...params);
  }

  static warnAndToast(...params: any[]): void {
    Log._log(LogLevel.Warn, ...params);
    toast.warn(params?.[0]);
  }

  static warnAndToastDetailed(...params: any[]): void {
    Log._log(LogLevel.Warn, ...params);
    toast.warn(<ToastErrorBox message={params?.[0]} error={params?.[1]} />);
  }

  static info(...params: any[]): void {
    Log._log(LogLevel.Info, ...params);
  }

  static infoAndToast(...params: any[]): void {
    Log._log(LogLevel.Info, ...params);
    toast.info(params?.[0]);
  }

  static infoAndToastDetailed(...params: any[]): void {
    Log._log(LogLevel.Info, ...params);
    toast.info(<ToastInfoBox message={params?.[0]} subMessage={params?.[1]} />);
  }

  static successAndToast(...params: any[]): void {
    Log._log(LogLevel.Info, ...params);
    toast.success(params?.[0]);
  }

  /** Success toast the user can act on; stays open longer than the default so the action stays reachable. */
  static successAndToastAction({
    message,
    actionLabel,
    onAction,
  }: {
    message: string;
    actionLabel: string;
    onAction: () => void;
  }): void {
    Log._log(LogLevel.Info, message);
    toast.success(
      <ToastActionBox
        message={message}
        actionLabel={actionLabel}
        onAction={onAction}
      />,
      { autoClose: TOAST_WITH_ACTION_AUTO_CLOSE_MS },
    );
  }

  static error(...params: any[]): void {
    Log._log(LogLevel.Error, ...params);
  }

  static errorAndToast(...params: any[]): void {
    Log._log(LogLevel.Error, ...params);
    toast.error(<ToastErrorBox message={params?.[0]} error={params?.[1]} />);
  }

  static errorAndMessageToast({
    error,
    message,
  }: {
    error: string | Error;
    message: string;
  }): void {
    Log._log(LogLevel.Error, ...[message, error]);
    toast.error(<ToastErrorBox message={message} />);
  }

  static debug(...params: any[]): void {
    if (isLocalOrJenkinsModeOrQsDebugLog()) {
      Log._log(LogLevel.Debug, ...params);
    }
  }

  static debugTable(...params: any[]): void {
    if (isLocalOrJenkinsMode()) {
      Log._log(LogLevel.Table, ...params);
    }
  }

  private static _log(...logData: any[]): void {
    const [level, ...allParams] = logData;
    // INFO: drop null/undefined args — they crash React DevTools' console
    // override (it scans args for `.stack`), which throws on nullish values.
    const params = allParams.filter(
      param => param !== undefined && param !== null,
    );

    switch (level) {
      case "info":
        console.info(...params);
        return;
      case "warn":
        console.warn(...params);
        return;
      case "error":
        console.error(...params);
        return;
      case "trace":
        console.trace(...params);
        return;
      case "log":
        console.log(...params);
        return;
      case "debug":
        console.debug(...params);
        return;
      case "table":
        console.table(...params);
    }
  }

  static count(...params: any[]): void {
    console.count(...params);
  }
}
